import {
    ChangeDetectionStrategy, Component, inject,
    output, signal, computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
    FieldExecutionImportService,
    SheetPreview, ImportResult, ImportActionPlan, ParsedGpRow, ParsedProjectRow,
    DuplicateHandling
} from '../../../services/field-execution-import.service';
import { FIELD_EXECUTION_BLOCKS } from '../../../services/field-execution.service';
import { normalizeGpLookupKey } from '../../../utils/field-execution-gp-validation.utils';

type ModalState = 'idle' | 'preview' | 'importing' | 'done';

@Component({
    selector: 'app-import-modal',
    imports: [CommonModule, FormsModule],
    templateUrl: './import-modal.component.html',
    styleUrl: './import-modal.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImportModalComponent {
    private readonly importSvc = inject(FieldExecutionImportService);

    readonly closed = output<boolean>();

    state              = signal<ModalState>('idle');
    sheets             = signal<SheetPreview[]>([]);
    parseErrors        = signal<string[]>([]);
    duplicateHandling  = signal<DuplicateHandling>('update');
    isDragging         = signal(false);
    importPlan         = signal<ImportActionPlan | null>(null);
    planError          = signal<string | null>(null);
    validationLoading  = signal(false);

    readonly hasGpPerSheetFormat = computed(() =>
        this.sheets().some(s => s.format === 'gp-per-sheet')
    );
    gpBlockName = signal('');
    readonly fieldExecutionBlocks = FIELD_EXECUTION_BLOCKS;

    readonly duplicateActionOptions: ReadonlyArray<{ value: DuplicateHandling; label: string }> = [
        { value: 'update', label: 'Update existing (refresh cost/name/fields + add new rows)' },
        { value: 'skip',   label: 'Skip duplicates (import only new rows)' },
        { value: 'allow',  label: 'Allow duplicates (import every row as a new record)' },
    ];

    readonly canStartImport = computed(() => {
        if (this.hasGpPerSheetFormat() && !this.gpBlockName()) return false;
        const plan = this.importPlan();
        return !!plan?.gpValidationPassed && !!plan?.canImport && !this.validationLoading();
    });

    progressDone  = signal(0);
    progressTotal = signal(0);
    progressPct   = computed(() =>
        this.progressTotal() > 0
            ? Math.round((this.progressDone() / this.progressTotal()) * 100)
            : 0
    );

    importResult = signal<ImportResult | null>(null);
    fileError    = signal<string | null>(null);

    onFileInputChange(event: Event): void {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (file) this.processFile(file);
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isDragging.set(true);
    }

    onDragLeave(): void { this.isDragging.set(false); }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        this.isDragging.set(false);
        const file = event.dataTransfer?.files?.[0];
        if (file) this.processFile(file);
    }

    private async processFile(file: File): Promise<void> {
        this.fileError.set(null);
        this.importPlan.set(null);
        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            this.fileError.set('Please select an Excel file (.xlsx or .xls).');
            return;
        }

        this.state.set('preview');
        try {
            const parsed = await this.importSvc.parseFile(file);
            if (parsed.length === 0) {
                this.fileError.set('No importable data found in this file.');
                this.state.set('idle');
                return;
            }
            this.sheets.set(parsed);
            this.parseErrors.set(parsed.flatMap(s => s.parseErrors));
            if (!parsed.some(s => s.format === 'gp-per-sheet')) {
                await this.refreshImportPlan();
            }
        } catch {
            this.fileError.set('Failed to read the file. Make sure it is a valid Excel file.');
            this.state.set('idle');
        }
    }

    async onBlockChange(block: string): Promise<void> {
        this.gpBlockName.set(block);
        await this.refreshImportPlan();
    }

    async onDuplicateActionChange(mode: string): Promise<void> {
        this.duplicateHandling.set(mode as DuplicateHandling);
        await this.refreshImportPlan();
    }

    async refreshImportPlan(): Promise<void> {
        const sheets = this.sheets();
        if (!sheets.length) {
            this.importPlan.set(null);
            return;
        }
        if (this.hasGpPerSheetFormat() && !this.gpBlockName()) {
            this.importPlan.set(null);
            return;
        }

        this.validationLoading.set(true);
        this.planError.set(null);
        try {
            const plan = await this.importSvc.computeImportPlan(sheets, {
                gpBlock: this.gpBlockName() || undefined,
                duplicateHandling: this.duplicateHandling(),
            });
            this.importPlan.set(plan);
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Import preview failed.';
            this.planError.set(msg);
            this.importPlan.set(null);
        } finally {
            this.validationLoading.set(false);
        }
    }

    isGpSheetInvalid(sheet: SheetPreview): boolean {
        const plan = this.importPlan();
        if (!plan?.gpValidation?.issues.length) return false;
        const gp = sheet.gpName || sheet.sheetName;
        const key = normalizeGpLookupKey(gp);
        return plan.gpValidation.issues.some(
            i => normalizeGpLookupKey(i.gpNameRaw) === key || i.sheetName === sheet.sheetName
        );
    }

    async startImport(): Promise<void> {
        await this.refreshImportPlan();
        const plan = this.importPlan();
        if (!plan?.canImport) return;

        this.state.set('importing');
        this.progressDone.set(0);
        this.progressTotal.set(plan.progressTotal);

        const result = await this.importSvc.importSheets(
            this.sheets(),
            {
                duplicateHandling: this.duplicateHandling(),
                gpBlock: this.gpBlockName() || undefined,
            },
            (done, total) => {
                this.progressDone.set(done);
                this.progressTotal.set(total);
            }
        );

        this.importResult.set(result);
        this.state.set('done');
    }

    async addSkipped(): Promise<void> {
        const result = this.importResult();
        if (!result || result.skippedProjects.length === 0) return;
        this.state.set('importing');
        this.progressDone.set(0);
        this.progressTotal.set(result.skippedProjects.length);
        const added = await this.importSvc.importSkipped(
            result.skippedProjects,
            (done, total) => { this.progressDone.set(done); this.progressTotal.set(total); }
        );
        this.importResult.set({
            ...result,
            imported: result.imported + added,
            skipped: Math.max(0, result.skipped - added),
            skippedItems: [],
            skippedProjects: [],
        });
        this.state.set('done');
    }

    close(): void { this.closed.emit(false); }
    closeAndRefresh(): void { this.closed.emit(true); }

    resetToIdle(): void {
        this.state.set('idle');
        this.sheets.set([]);
        this.parseErrors.set([]);
        this.fileError.set(null);
        this.importResult.set(null);
        this.importPlan.set(null);
        this.planError.set(null);
        this.duplicateHandling.set('update');
        this.gpBlockName.set('');
    }

    previewGpRows(sheet: SheetPreview, max = 5): ParsedGpRow[] {
        return sheet.gpRows.slice(0, max);
    }

    previewProjectRows(sheet: SheetPreview, max = 5): ParsedProjectRow[] {
        return sheet.projectRows.slice(0, max);
    }

    totalRowsFound(): number {
        return this.importPlan()?.totalRowsFound
            ?? this.sheets().reduce((s, sh) => s + sh.totalRows, 0);
    }
}
